'use client'

import { useState, useEffect, Suspense } from 'react'
import Link from 'next/link'
import { useRouter, useSearchParams } from 'next/navigation'
import { useAuth } from '@/context/AuthContext'
import { register as apiRegister, login as apiLogin, getCurrentUser } from '@/lib/api'
import { AuthShell, GoogleButton } from '@/components/shop/AuthShell'

function RegisterForm() {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const redirect = searchParams.get('redirect') || '/'
  const { login, user, isLoading } = useAuth()

  useEffect(() => {
    if (!isLoading && user) {
      router.push(redirect)
    }
  }, [user, isLoading, router, redirect])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (password !== confirmPassword) return setError('Lösenorden matchar inte')
    if (password.length < 8) return setError('Lösenordet måste vara minst 8 tecken')
    if (!/[A-Z]/.test(password)) return setError('Lösenordet måste innehålla en stor bokstav')
    if (!/[a-z]/.test(password)) return setError('Lösenordet måste innehålla en liten bokstav')
    if (!/[0-9]/.test(password)) return setError('Lösenordet måste innehålla en siffra')

    setIsSubmitting(true)
    try {
      await apiRegister({ email, password, name: name || undefined })
      await apiLogin(email, password)
      const userData = await getCurrentUser()
      login(userData)
      router.push(redirect)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registrering misslyckades')
    } finally {
      setIsSubmitting(false)
    }
  }

  const inputClass =
    'w-full h-12 px-4 bg-neutral-50 dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl text-neutral-900 dark:text-white placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900 dark:focus:ring-white/30 transition-all'

  return (
    <AuthShell title="Skapa ditt konto" subtitle="Kom igång och börja handla">
      <GoogleButton redirect={redirect} label="Registrera med Google" />

      <div className="flex items-center gap-3 my-6">
        <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-800" />
        <span className="text-xs text-neutral-400">eller</span>
        <div className="flex-1 h-px bg-neutral-200 dark:bg-neutral-800" />
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm">
            {error}
          </div>
        )}
        <div>
          <label htmlFor="name" className="block text-sm font-medium mb-2">Namn <span className="text-neutral-400">(valfritt)</span></label>
          <input id="name" type="text" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} placeholder="Anna Andersson" />
        </div>
        <div>
          <label htmlFor="email" className="block text-sm font-medium mb-2">E-post</label>
          <input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} placeholder="du@example.com" required />
        </div>
        <div>
          <label htmlFor="password" className="block text-sm font-medium mb-2">Lösenord</label>
          <input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputClass} placeholder="••••••••" minLength={8} required />
          <p className="text-xs text-neutral-400 mt-1">Minst 8 tecken med stor & liten bokstav samt siffra</p>
        </div>
        <div>
          <label htmlFor="confirmPassword" className="block text-sm font-medium mb-2">Bekräfta lösenord</label>
          <input id="confirmPassword" type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className={inputClass} placeholder="••••••••" required />
        </div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full h-12 bg-neutral-900 dark:bg-white text-white dark:text-neutral-900 font-medium rounded-xl hover:bg-neutral-800 dark:hover:bg-neutral-100 transition-colors disabled:opacity-50"
        >
          {isSubmitting ? 'Skapar konto…' : 'Skapa konto'}
        </button>
      </form>

      <p className="text-center mt-6 text-sm text-neutral-500 dark:text-neutral-400">
        Har du redan ett konto?{' '}
        <Link href={`/login${redirect !== '/' ? `?redirect=${encodeURIComponent(redirect)}` : ''}`} className="text-neutral-900 dark:text-white font-medium hover:underline">
          Logga in
        </Link>
      </p>
    </AuthShell>
  )
}

export default function RegisterPage() {
  return (
    <Suspense fallback={<div className="min-h-screen bg-white dark:bg-neutral-950" />}>
      <RegisterForm />
    </Suspense>
  )
}
